// components/SupplierFinancials.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Payment,
  Receipt,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  Schedule,
  CheckCircle,
  Error as ErrorIcon,
  Visibility,
  Add,
  AccountBalanceWallet,
  ReceiptLong,
  AttachMoney,
  Warning,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} {...other}>
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

// Enhanced Card Component with better styling
const FinancialCard = ({ icon, title, value, subtitle, color, trend, chip }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.1)} 0%, ${alpha(theme.palette[color].main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette[color].main, 0.15)}`,
        }
      }}
    >
      <CardContent sx={{ textAlign: "center", p: 3 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette[color].main, 0.1),
            mb: 2,
            color: theme.palette[color].main,
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 30 } })}
        </Box>
        
        <Typography 
          variant="h3" 
          fontWeight="800"
          color={color + '.main'}
          sx={{ 
            fontSize: { xs: '2rem', md: '2.5rem' },
            mb: 1
          }}
        >
          ₹{(value || 0).toLocaleString()}
        </Typography>
        
        <Typography 
          variant="h6" 
          fontWeight="600"
          color="text.primary"
          sx={{ mb: 1 }}
        >
          {title}
        </Typography>
        
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ mb: 2, fontSize: '0.9rem' }}
        >
          {subtitle}
        </Typography>
        
        {chip && (
          <Chip 
            {...chip}
            sx={{ 
              borderRadius: 2,
              fontWeight: '600',
              fontSize: '0.75rem',
              height: 28
            }}
          />
        )}
        
        {trend && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
            {trend.direction === 'up' ? (
              <ArrowUpward sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
            ) : (
              <ArrowDownward sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
            )}
            <Typography 
              variant="body2" 
              color={trend.direction === 'up' ? 'success.main' : 'error.main'}
              fontWeight="600"
            >
              {trend.value}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const InvSupplierFinancials = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [financialData, setFinancialData] = useState(null);
  const [grns, setGrns] = useState([]);
  const [unpaidGrns, setUnpaidGrns] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [quickPayment, setQuickPayment] = useState({
    amount: "",
    paymentMethod: "bank_transfer",
    paymentDate: new Date().toISOString().split("T")[0],
    reference: "",
    notes: "",
  });

  useEffect(() => {
    fetchFinancialData();
    fetchGRNs();
    fetchUnpaidGRNs();
    fetchPayments();
  }, [id]);

  const fetchFinancialData = async () => {
    try {
      const response = await suppliersAPI.getFinancialSummary(id);
      setFinancialData(response.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching financial data", { variant: "error" });
    }
  };

  const fetchGRNs = async () => {
    try {
      const response = await suppliersAPI.getGRNs(id);
      setGrns(response.data.data.grns || []);
    } catch (error) {
      enqueueSnackbar("Error fetching GRNs", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchUnpaidGRNs = async () => {
    try {
      const response = await suppliersAPI.getUnpaidGRNs(id);
      setUnpaidGrns(response.data.data.unpaidGRNs || []);
    } catch (error) {
      console.error("Error fetching unpaid GRNs:", error);
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await suppliersAPI.getPayments(id);
      setPayments(response.data.data.payments || []);
    } catch (error) {
      console.error("Error fetching payments:", error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: "success",
      draft: "default",
      received: "info",
      verified: "warning",
      pending: "warning",
      paid: "success",
      partially_paid: "info",
      unpaid: "error",
    };
    return colors[status] || "default";
  };

  const handleViewGRN = (grn) => {
  // Navigate to GRN view through purchase order route
  if (grn.purchaseOrder && grn.purchaseOrder._id) {
    navigate(`/hospital/hospital-inventory/purchases/${grn.purchaseOrder._id}/grn`);
  } else if (grn.purchaseOrder) {
    // If purchaseOrder is just an ID string
    navigate(`/hospital/hospital-inventory/purchases/${grn.purchaseOrder}/grn`);
  } else {
    // Fallback: show error message
    enqueueSnackbar("Cannot view GRN: Purchase Order information missing", { 
      variant: "error" 
    });
  }
};

  const handlePayGRN = (grn) => {
    setSelectedGRN(grn);
    const balance = grn.totalAmount - (grn.paidAmount || 0);
    setQuickPayment({
      amount: balance > 0 ? balance : 0,
      paymentMethod: "bank_transfer",
      paymentDate: new Date().toISOString().split("T")[0],
      reference: "",
      notes: `Payment for GRN ${grn.grnNumber}`,
    });
    setPaymentDialog(true);
  };

  const handleQuickPayment = async () => {
    try {
      const paymentData = {
        ...quickPayment,
        amount: parseFloat(quickPayment.amount),
        paymentType: "grn_payment",
        status: "completed",
        grnId: selectedGRN._id,
      };

      await suppliersAPI.addPayment(id, paymentData);
      
      enqueueSnackbar("Payment recorded successfully", { variant: "success" });
      setPaymentDialog(false);
      setSelectedGRN(null);
      
      // Refresh data
      fetchFinancialData();
      fetchGRNs();
      fetchUnpaidGRNs();
      fetchPayments();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || "Error recording payment",
        { variant: "error" }
      );
    }
  };

  const handleNavigateToPayments = () => {
    navigate(`/suppliers/${id}/payments`);
  };

  const handleNavigateToReturns = () => {
    navigate(`/suppliers/${id}/returns`);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box  
      sx={{
        minHeight:'100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
    <Box sx={{ p: 4 }}>
      {/* Enhanced Header with Gradient Background */}
      <Card 
        sx={{ 
          mb: 4,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography 
                variant="h2" 
                fontWeight="800"
                color="primary.main"
                sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}
              >
                Financial Overview
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: '1.1rem', opacity: 0.8 }}
              >
                Complete financial summary and payment management
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ReceiptLong sx={{ fontSize: 24 }} />}
                onClick={handleNavigateToReturns}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                View Returns
              </Button>
              <Button
                variant="contained"
                startIcon={<Payment sx={{ fontSize: 24 }} />}
                onClick={handleNavigateToPayments}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: '600',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                }}
              >
                Manage Payments
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Enhanced Financial Summary Cards */}
      {financialData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <FinancialCard
              icon={<Receipt />}
              title="Total GRN Value"
              value={financialData.financialSummary?.totalGRNValue}
              subtitle="All received goods"
              color="primary"
              chip={{
                label: `${financialData.financialSummary?.grnCount || 0} GRNs`,
                variant: "outlined",
                color: "primary"
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FinancialCard
              icon={<Payment />}
              title="Total Payments"
              value={financialData.financialSummary?.totalPayments}
              subtitle="All transactions"
              color="success"
              chip={{
                label: `${financialData.paymentTypeBreakdown?.length || 0} transactions`,
                color: "success"
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FinancialCard
              icon={<AccountBalanceWallet />}
              title="Outstanding Balance"
              value={financialData.financialSummary?.outstandingBalance}
              subtitle="Pending payments"
              color="warning"
              chip={
                financialData.financialSummary?.outstandingBalance > 0 ? {
                  icon: <Warning />,
                  label: "Payment Due",
                  color: "error"
                } : undefined
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FinancialCard
              icon={<Schedule />}
              title="Unpaid GRNs"
              value={financialData.financialSummary?.pendingGRNValue}
              subtitle="Require payment"
              color="error"
              chip={{
                label: `${financialData.financialSummary?.unpaidGRNCount || 0} pending`,
                color: "error"
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <FinancialCard
              icon={<TrendingUp />}
              title="Pending POs"
              value={financialData.financialSummary?.pendingPOValue}
              subtitle="Future commitments"
              color="info"
              chip={{
                label: `${financialData.financialSummary?.pendingPOCount || 0} orders`,
                color: "info"
              }}
            />
          </Grid>
        </Grid>
      )}

      {/* Enhanced Tabs with Better Styling */}
      <Card 
        sx={{ 
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Tabs 
            value={tabValue} 
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: '600',
                py: 2,
                px: 3,
                minHeight: 60,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                }
              },
              '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: '4px 4px 0 0',
              }
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt sx={{ fontSize: 20 }} />
                  <span>All GRNs</span>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning sx={{ fontSize: 20 }} />
                  <span>Unpaid GRNs</span>
                </Box>
              } 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Payment sx={{ fontSize: 20 }} />
                  <span>Recent Payments</span>
                </Box>
              } 
            />
          </Tabs>
        </CardContent>
      </Card>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <GRNTable 
          grns={grns} 
          onView={handleViewGRN}
          onPay={handlePayGRN}
          getStatusColor={getStatusColor}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <GRNTable 
          grns={unpaidGrns} 
          onView={handleViewGRN}
          onPay={handlePayGRN}
          getStatusColor={getStatusColor}
          showPayButton={true}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <PaymentTable payments={payments} />
      </TabPanel>

      {/* Enhanced Quick Payment Dialog */}
      <Dialog 
        open={paymentDialog} 
        onClose={() => setPaymentDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          }
        }}
      >
        <DialogTitle sx={{ 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          py: 3
        }}>
          <Typography variant="h4" fontWeight="700" color="primary.main">
            Quick Payment
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
            {selectedGRN?.grnNumber}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 2,
                  fontSize: '1rem',
                  '& .MuiAlert-icon': { fontSize: 28 }
                }}
              >
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Payment Summary
                </Typography>
                GRN Amount: <strong>₹{selectedGRN?.totalAmount?.toLocaleString()}</strong>
                {selectedGRN?.paidAmount > 0 && (
                  <span> (Already Paid: <strong>₹{selectedGRN.paidAmount?.toLocaleString()}</strong>)</span>
                )}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Payment Amount"
                value={quickPayment.amount}
                onChange={(e) => setQuickPayment(prev => ({ ...prev, amount: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  sx: { fontSize: '1.1rem', borderRadius: 2 }
                }}
                InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '1.1rem' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={quickPayment.paymentMethod}
                onChange={(e) => setQuickPayment(prev => ({ ...prev, paymentMethod: e.target.value }))}
                InputProps={{ sx: { borderRadius: 2, fontSize: '1.1rem' } }}
                InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
              >
                <MenuItem value="bank_transfer" sx={{ fontSize: '1rem' }}>Bank Transfer</MenuItem>
                <MenuItem value="cheque" sx={{ fontSize: '1rem' }}>Cheque</MenuItem>
                <MenuItem value="cash" sx={{ fontSize: '1rem' }}>Cash</MenuItem>
                <MenuItem value="upi" sx={{ fontSize: '1rem' }}>UPI</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Payment Date"
                value={quickPayment.paymentDate}
                onChange={(e) => setQuickPayment(prev => ({ ...prev, paymentDate: e.target.value }))}
                InputLabelProps={{ 
                  shrink: true,
                  sx: { fontSize: '1.1rem' }
                }}
                InputProps={{ sx: { borderRadius: 2, fontSize: '1.1rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference"
                value={quickPayment.reference}
                onChange={(e) => setQuickPayment(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Transaction ID, Cheque No."
                InputProps={{ sx: { borderRadius: 2, fontSize: '1.1rem' } }}
                InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={quickPayment.notes}
                onChange={(e) => setQuickPayment(prev => ({ ...prev, notes: e.target.value }))}
                InputProps={{ sx: { borderRadius: 2, fontSize: '1.1rem' } }}
                InputLabelProps={{ sx: { fontSize: '1.1rem' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setPaymentDialog(false)} 
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1,
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleQuickPayment} 
            variant="contained"
            disabled={!quickPayment.amount || quickPayment.amount <= 0}
            sx={{ 
              borderRadius: 2,
              px: 4,
              py: 1,
              fontSize: '1rem',
              fontWeight: '600',
              background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
            }}
          >
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </Box>
  );
};

// Enhanced GRN Table Component
const GRNTable = ({ grns, onView, onPay, getStatusColor, showPayButton = false }) => {
  const theme = useTheme();
  
  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h3" fontWeight="700" color="primary.main">
            Goods Received Notes
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" color="text.secondary" fontWeight="600">
              {grns.length} records
            </Typography>
            {showPayButton && grns.length > 0 && (
              <Chip 
                label={`Total Due: ₹${grns.reduce((sum, grn) => sum + (grn.totalAmount - (grn.paidAmount || 0)), 0).toLocaleString()}`} 
                color="error" 
                variant="filled"
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: '700',
                  height: 40,
                  px: 2
                }}
              />
            )}
          </Box>
        </Box>

        {grns.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              borderRadius: 2,
              fontSize: '1.1rem',
              py: 2
            }}
          >
            No GRNs found.
          </Alert>
        ) : (
          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` 
                }}>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>GRN Number</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>PO Number</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Receipt Date</TableCell>
                  <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Total Amount</TableCell>
                  <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Paid Amount</TableCell>
                  <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Balance</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Payment Status</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {grns.map((grn) => {
                  const paidAmount = grn.paidAmount || 0;
                  const balance = grn.totalAmount - paidAmount;
                  
                  return (
                    <TableRow 
                      key={grn._id} 
                      hover
                      sx={{ 
                        '&:last-child td': { borderBottom: 0 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        }
                      }}
                    >
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="h6" fontWeight="700" color="primary.main">
                          {grn.grnNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2.5, fontSize: '1rem' }}>
                        {grn.purchaseOrder?.poNumber || 'N/A'}
                      </TableCell>
                      <TableCell sx={{ py: 2.5, fontSize: '1rem' }}>
                        {new Date(grn.receiptDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2.5 }}>
                        <Typography variant="h6" fontWeight="700">
                          ₹{grn.totalAmount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2.5 }}>
                        <Typography variant="h6" color="success.main" fontWeight="600">
                          ₹{paidAmount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2.5 }}>
                        <Typography 
                          variant="h6" 
                          fontWeight="700"
                          color={balance > 0 ? "error.main" : "success.main"}
                        >
                          ₹{balance.toLocaleString()}
                        </Typography>
                      </TableCell>
                      
                      <TableCell sx={{ py: 2.5 }}>
                        {balance === 0 ? (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 18 }} />}
                            label="Paid"
                            size="medium"
                            color="success"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              height: 32
                            }}
                          />
                        ) : paidAmount > 0 ? (
                          <Chip
                            icon={<Schedule sx={{ fontSize: 18 }} />}
                            label="Partial"
                            size="medium"
                            color="warning"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              height: 32
                            }}
                          />
                        ) : (
                          <Chip
                            icon={<ErrorIcon sx={{ fontSize: 18 }} />}
                            label="Unpaid"
                            size="medium"
                            color="error"
                            sx={{ 
                              fontSize: '0.9rem',
                              fontWeight: '600',
                              height: 32
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View GRN Details">
                            <IconButton
                              size="large"
                              onClick={() => onView(grn)}
                              color="primary"
                              sx={{ 
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                }
                              }}
                            >
                              <Visibility sx={{ fontSize: 22 }} />
                            </IconButton>
                          </Tooltip>
                          {showPayButton && balance > 0 && (
                            <Tooltip title="Make Payment">
                              <IconButton
                                size="large"
                                onClick={() => onPay(grn)}
                                color="success"
                                sx={{ 
                                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.success.main, 0.2),
                                  }
                                }}
                              >
                                <Payment sx={{ fontSize: 22 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Enhanced Payment Table Component
const PaymentTable = ({ payments }) => {
  const theme = useTheme();
  
  const getPaymentTypeColor = (type) => {
    const colors = {
      grn_payment: "primary",
      advance_payment: "info",
      return_settlement: "success",
      credit_note: "success",
      account_settlement: "secondary",
    };
    return colors[type] || "default";
  };

  return (
    <Card
      sx={{
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h3" gutterBottom fontWeight="700" color="primary.main">
          Recent Payments
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {payments.length} transactions found
        </Typography>

        {payments.length === 0 ? (
          <Alert 
            severity="info" 
            sx={{ 
              borderRadius: 2,
              fontSize: '1.1rem',
              py: 2
            }}
          >
            No payments found.
          </Alert>
        ) : (
          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` 
                }}>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Date</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Type</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Reference</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Method</TableCell>
                  <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '1.1rem', fontWeight: '700', py: 3 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.slice(0, 10).map((payment) => (
                  <TableRow 
                    key={payment._id} 
                    hover
                    sx={{ 
                      '&:last-child td': { borderBottom: 0 },
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      }
                    }}
                  >
                    <TableCell sx={{ py: 2.5, fontSize: '1rem' }}>
                      {new Date(payment.paymentDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={payment.paymentType?.replace(/_/g, " ") || "Payment"}
                        size="medium"
                        color={getPaymentTypeColor(payment.paymentType)}
                        sx={{ 
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          height: 32
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2.5, fontSize: '1rem' }}>
                      {payment.reference || "N/A"}
                    </TableCell>
                    <TableCell sx={{ py: 2.5, fontSize: '1rem' }}>
                      {payment.paymentMethod?.replace(/_/g, " ") || "N/A"}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2.5 }}>
                      <Typography variant="h6" fontWeight="700" color="success.main">
                        ₹{payment.amount?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2.5 }}>
                      <Chip
                        label={payment.status || "completed"}
                        size="medium"
                        color={payment.status === 'completed' ? 'success' : 'warning'}
                        sx={{ 
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          height: 32
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default InvSupplierFinancials;